/**
 * Report voice layer — structured, actionable interpretation from analysis + theme objects.
 * Calculation logic lives in calculations/themeDetection; this file only composes copy.
 */

import { TIERS, SECTION_KEYS, tierAllows } from './tiers';
import { reduceNumber, digitSum } from './reductionRules';
import {
  buildDigitBlockCopy,
  packForDigit,
  digitWord,
  familyPlainName,
} from './reportNarrative';
import { buildAdvancedCourseParityTopics } from './advancedCourseReportLinks';

const MONTH_NAMES = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function joinSentences(parts) {
  return parts.filter(Boolean).join(' ');
}

function reducedYearComponent(year) {
  const y = Math.floor(Number(year));
  const first = digitSum(y);
  return reduceNumber(first).value;
}

function structuralPlain(theme) {
  const top = theme.topFamilies[0];
  const second = theme.topFamilies[1];
  const l1 = familyPlainName(top, theme.familyLabels);
  const l2 = second ? familyPlainName(second, theme.familyLabels) : '';
  if (theme.structuralType.startsWith('concentrated_')) {
    return `Your chart leans hard toward ${l1} — that thread runs through multiple positions.`;
  }
  if (theme.structuralType.startsWith('dual_emphasis_') && l2) {
    return `You carry both ${l1} and ${l2} with real weight; neither is a background detail.`;
  }
  return `Several themes share the stage, so you may notice more than one inner priority at once.`;
}

export function buildHowToRead() {
  return [
    'Numerology here is read as a pattern language: numbers describe tendencies and recurring motifs, not fixed identity.',
    'You might treat this report like a map of emphasis — useful for reflection, not a verdict on who you are.',
    'When a theme repeats, it is usually worth attention. When two themes pull in different directions, that tension can be informative rather than problematic.',
  ];
}

export function buildHowToReadMini() {
  return [
    'Numbers here describe tendencies and motifs, not a fixed identity.',
    'Treat this as a map for reflection — useful when read calmly, not as a verdict.',
  ];
}

export function buildPatternAtAGlance(bundle, theme) {
  const { chartBalance, topFamilies, familyLabels, masterEcho, repeatedCoreDigits, karmicDebt } = theme;
  const lines = [];
  lines.push(structuralPlain(theme));
  lines.push(
    `The name grid looks ${chartBalance === 'sparse' ? 'wide and varied' : chartBalance === 'concentrated' ? 'relatively concentrated' : 'mixed'}, which shapes how quickly certain habits feel familiar.`,
  );
  if (topFamilies[0]) {
    lines.push(
      `Across weighted indicators, the strongest symbolic families include ${topFamilies
        .slice(0, 3)
        .map((f) => familyLabels[f])
        .join(', ')}.`,
    );
  }
  if (repeatedCoreDigits.length) {
    lines.push(
      `Repeated digits among core numbers (${repeatedCoreDigits.join(', ')}) make a motif easier to spot because it shows up in more than one lane.`,
    );
  }
  if (masterEcho) {
    lines.push(
      'A master number appears somewhere in the core profile. Certain lessons may feel vivid — not necessarily easy, but difficult to ignore.',
    );
  }
  if (karmicDebt?.length) {
    lines.push(
      `Karmic debt markers (${karmicDebt.join(', ')}) are noted where they appear in reduction chains — naming lessons that may ask for patience and honesty rather than speed.`,
    );
  }
  return lines;
}

export function buildReportSummary(bundle, theme) {
  const meta = bundle.meta;
  const n = bundle.numbers;
  const name = meta.firstName || 'Your chart';
  const summaryBadge = 'Personal numerology report';

  const keyNumbers = [
    { label: 'Life Path', value: n.lifePath.value, isMaster: n.lifePath.isMaster, hint: 'Long arc' },
    {
      label: 'Expression (Destiny)',
      value: n.expression.value,
      isMaster: n.expression.isMaster,
      hint: 'Name path',
    },
    { label: 'Soul Urge', value: n.soulUrge.value, isMaster: n.soulUrge.isMaster, hint: 'Inner drive' },
    { label: 'Personality', value: n.personality.value, isMaster: n.personality.isMaster, hint: 'Outer style' },
    { label: 'Birthday', value: n.birthday.value, isMaster: n.birthday.isMaster, hint: 'Day gift tone' },
    { label: 'Maturity', value: n.maturity.value, isMaster: n.maturity.isMaster, hint: 'Later integration' },
    {
      label: 'Personal Year',
      value: n.personalYear.value,
      isMaster: n.personalYear.isMaster,
      hint: `Timing · ${meta.targetYear}`,
    },
  ];

  const introParts = [structuralPlain(theme)];
  const f1 = theme.topFamilies[0] ? familyPlainName(theme.topFamilies[0], theme.familyLabels) : '';
  if (f1) {
    introParts.push(
      `Taken together, the strongest thread is ${f1}${
        theme.topFamilies[1]
          ? `, alongside ${familyPlainName(theme.topFamilies[1], theme.familyLabels)}`
          : ''
      }.`,
    );
  }
  if (theme.repeatedCoreDigits.length) {
    introParts.push(
      `The digits ${theme.repeatedCoreDigits.join(', ')} repeat across core positions — what they describe can feel like a default setting, not a passing mood.`,
    );
  }
  if (theme.masterEcho) {
    introParts.push(
      'A master number in the core profile often means certain themes refuse to stay theoretical; they show up in real choices.',
    );
  }
  introParts.push(
    'Use this report to see where you over-function, where you stall, and where your strengths actually belong.',
  );

  return {
    summaryBadge,
    displayName: name,
    timingYear: meta.targetYear,
    birthDateLabel: meta.birthDate || null,
    keyNumbers,
    intro: joinSentences(introParts),
  };
}

export function buildChartOneSentence(bundle, theme) {
  const n = bundle.numbers;
  const lp = n.lifePath.value;
  const su = n.soulUrge.value;
  const ex = n.expression.value;
  const words = [...new Set([digitWord(lp), digitWord(su), digitWord(ex)])];
  const cluster =
    words.length >= 3
      ? `${words[0]}, ${words[1]}, and ${words[2]}`
      : words.length === 2
        ? `${words[0]} and ${words[1]}`
        : words[0] || 'distinctive mix';

  let tensionBit = 'the moving parts of your chart mostly ask for the same kind of integrity.';
  const t0 = theme.tensions[0];
  if (t0?.id === 'desire_path_gap') {
    tensionBit = `your chart also marks friction between inner motivation (Soul Urge ${su}) and the longer storyline (Life Path ${lp}).`;
  } else if (t0?.id === 'outer_inner_gap') {
    tensionBit = `your chart also marks friction between what you show first (Personality ${n.personality.value}) and what you actually want (Soul Urge ${su}).`;
  } else if (t0?.id === 'expression_path_gap') {
    tensionBit = `your chart also marks friction between how your name-path moves (Expression ${ex}) and the arc you are living (Life Path ${lp}).`;
  }

  const exW = digitWord(ex);
  const suW = digitWord(su);
  const gift =
    exW === suW
      ? `Your most workable edge is often ${exW} held steady in real time — not only in intention.`
      : `Your most workable edge is often the blend of ${exW} in action and ${suW} underneath.`;

  return joinSentences([
    `You are organized around ${cluster}, and ${tensionBit}`,
    gift,
  ]);
}

/**
 * Three-block snapshot: driver / challenge / gift — derived from theme + existing digit packs (no new math).
 */
export function buildCorePattern(bundle, theme) {
  const n = bundle.numbers;
  const lp = n.lifePath.value;
  const topFam = theme.topFamilies[0];
  const famLabel = topFam ? familyPlainName(topFam, theme.familyLabels) : 'your leading themes';

  let driverBody;
  if (theme.repeatedCoreDigits.length) {
    const d = theme.repeatedCoreDigits[0];
    const p = packForDigit(d);
    driverBody = joinSentences([
      `What drives you most is repeated ${p.word} energy: ${d} appears in more than one core position, so you keep reinvesting in the same kind of fix when pressure hits.`,
      `You default to ${p.showsUp[0].charAt(0).toLowerCase() + p.showsUp[0].slice(1)}`,
    ]);
  } else if (theme.structuralType?.startsWith('concentrated_')) {
    const p = packForDigit(lp);
    driverBody = joinSentences([
      `Your chart concentrates around ${famLabel}. Life Path ${lp} carries that concentration in the long arc (${digitWord(lp)}).`,
      `When it counts, you lead with ${p.word}: ${p.showsUp[1] ? p.showsUp[1].charAt(0).toLowerCase() + p.showsUp[1].slice(1) : p.showsUp[0].charAt(0).toLowerCase() + p.showsUp[0].slice(1)}`,
    ]);
  } else {
    const p = packForDigit(lp);
    driverBody = joinSentences([
      `Your core driver is ${famLabel}, anchored by Life Path ${lp} (${digitWord(lp)}).`,
      `You reach for ${p.word} when you care: ${p.showsUp[0].charAt(0).toLowerCase() + p.showsUp[0].slice(1)}`,
    ]);
  }

  const t0 = theme.tensions[0];
  let challengeBody;
  if (t0?.id === 'desire_path_gap') {
    challengeBody = joinSentences([
      `You get stuck when Soul Urge ${n.soulUrge.value} and Life Path ${lp} disagree on pacing — you keep acting loyal to a storyline your gut has already revised.`,
      `Effort feels hollow when inner willingness is not in the room.`,
    ]);
  } else if (t0?.id === 'outer_inner_gap') {
    challengeBody = joinSentences([
      `You take responsibility for the emotional temperature before you say what you need (Personality ${n.personality.value} versus Soul Urge ${n.soulUrge.value}).`,
      `You perform “fine,” then wonder why resentment arrived without a clear cause.`,
    ]);
  } else if (t0?.id === 'expression_path_gap') {
    challengeBody = joinSentences([
      `You tie visible output to safety: Expression ${n.expression.value} pushes to prove capacity while Life Path ${lp} asks for a slower, truer tempo.`,
      `You stall when every win has to redeem the whole arc immediately.`,
    ]);
  } else {
    const p = packForDigit(lp);
    challengeBody = joinSentences([
      `Your main snag is the ${p.word} shadow: ${p.watchFor[0]}, then ${p.watchFor[1].toLowerCase()}.`,
      `The same strength over-cranked until boundaries show up late.`,
    ]);
  }

  let giftBody;
  if (theme.repeatedCoreDigits.length) {
    const d = theme.repeatedCoreDigits[0];
    const p = packForDigit(d);
    giftBody = joinSentences([
      `Your strongest asset is ${p.word} when you choose it instead of defaulting to it: ${p.useWell[0]} ${p.useWell[1].charAt(0).toLowerCase() + p.useWell[1].slice(1)}`,
    ]);
  } else {
    const pEx = packForDigit(n.expression.value);
    const pSu = packForDigit(n.soulUrge.value);
    giftBody = joinSentences([
      `Your clearest leverage is ${digitWord(n.expression.value)} in motion (Expression ${n.expression.value}) fueled by ${digitWord(n.soulUrge.value)} (Soul Urge ${n.soulUrge.value}).`,
      `${pEx.useWell[0]} ${pSu.useWell[0].charAt(0).toLowerCase() + pSu.useWell[0].slice(1)}`,
    ]);
  }

  return [
    { key: 'driver', title: 'Core driver', body: driverBody },
    { key: 'challenge', title: 'Main challenge', body: challengeBody },
    { key: 'gift', title: 'Strongest gift', body: giftBody },
  ];
}

function coreBlock(key, label, value, isMaster) {
  const copy = buildDigitBlockCopy(value, isMaster);
  return {
    key,
    label,
    value,
    isMaster,
    ...copy,
  };
}

function personalMonthCoreBlock(bundle) {
  const n = bundle.numbers;
  const m = MONTH_NAMES[n.personalMonth.month] || n.personalMonth.month;
  const b = coreBlock('personalMonth', 'Personal Month', n.personalMonth.value, n.personalMonth.isMaster);
  b.coreMeaning = joinSentences([
    b.coreMeaning,
    `Blends Personal Year ${n.personalYear.value} with calendar month ${n.personalMonth.month} (${m}). Treat it as a short pacing layer inside the year, not a separate destiny.`,
  ]);
  return b;
}

export function buildCoreInterpretationBlocks(bundle) {
  const n = bundle.numbers;
  const blocks = [
    coreBlock('lifePath', 'Life Path', n.lifePath.value, n.lifePath.isMaster),
    coreBlock('expression', 'Expression (Destiny)', n.expression.value, n.expression.isMaster),
    coreBlock('soulUrge', 'Soul Urge', n.soulUrge.value, n.soulUrge.isMaster),
    coreBlock('personality', 'Personality', n.personality.value, n.personality.isMaster),
    coreBlock('birthday', 'Birthday', n.birthday.value, n.birthday.isMaster),
    coreBlock('maturity', 'Maturity', n.maturity.value, n.maturity.isMaster),
    coreBlock('personalYear', 'Personal Year', n.personalYear.value, n.personalYear.isMaster),
    personalMonthCoreBlock(bundle),
  ];

  if (n.currentExpression && n.currentExpression.value !== n.expression.value) {
    blocks.push(
      coreBlock(
        'currentExpression',
        'Current name Expression',
        n.currentExpression.value,
        n.currentExpression.isMaster,
      ),
    );
  }

  return blocks;
}

export function buildRepeatingPatterns(bundle, theme) {
  const patterns = [];
  const { repeatedCoreDigits, masterEcho, topFamilies, familyLabels, structuralType } = theme;
  const n = bundle.numbers;

  repeatedCoreDigits.forEach((d) => {
    const p = packForDigit(d);
    patterns.push({
      title: `Repeated ${d} · ${p.word}`,
      whyItRepeats: `The digit ${d} appears in more than one core position, so ${p.word} is not a side note — it is a recurring ingredient in how you decide, respond, and commit.`,
      feelsLike: `You may notice the same dilemma returning with new faces: ${p.watchFor[0].toLowerCase()} shows up, then ${p.watchFor[1].toLowerCase()}.`,
    });
  });

  if (masterEcho) {
    patterns.push({
      title: 'Master number pressure',
      whyItRepeats:
        'A master number (11, 22, or 33) in the core profile concentrates intensity: the underlying digit theme runs hotter and asks for cleaner honesty.',
      feelsLike:
        'Less tolerance for half-measures; lessons can feel public or urgent even when the situation looks ordinary.',
    });
  }

  if (structuralType.startsWith('concentrated_') && topFamilies[0]) {
    const fam = familyPlainName(topFamilies[0], familyLabels);
    patterns.push({
      title: `Single-family weight · ${fam}`,
      whyItRepeats: `Weighted scores cluster heavily in one family, so ${fam} is not one “chapter” — it is closer to your operating system.`,
      feelsLike:
        'Strengths and blind spots may share a root: the same quality that helps you can also exhaust you when it runs without limits.',
    });
  }

  const suFam = theme.familyLabels[theme.topFamilies.find((f) => f) || ''] || '';
  if (
    patterns.length < 2 &&
    Math.abs(n.soulUrge.value - n.personality.value) >= 3 &&
    !repeatedCoreDigits.length
  ) {
    patterns.push({
      title: 'Inner vs outer calibration',
      whyItRepeats: `Soul Urge ${n.soulUrge.value} and Personality ${n.personality.value} sit far apart on the number line, so inner priorities and social presentation may be trained on different questions.`,
      feelsLike:
        'You might feel “fine” in public while privately depleted — or energized alone and strained in performance mode.',
    });
  }

  if (!patterns.length) {
    patterns.push({
      title: 'Distributed chart',
      whyItRepeats:
        'No single digit dominates the core set; themes are spread across several families, which can make identity feel layered rather than singular.',
      feelsLike:
        'You might rotate between modes (structure, connection, movement) depending on context instead of living one constant speed.',
    });
  }

  return patterns.slice(0, 5);
}

function tensionPoles(tensionId, bundle) {
  const n = bundle.numbers;
  if (tensionId === 'desire_path_gap') {
    return {
      poleA: `Soul Urge ${n.soulUrge.value} (${digitWord(n.soulUrge.value)})`,
      poleB: `Life Path ${n.lifePath.value} (${digitWord(n.lifePath.value)})`,
    };
  }
  if (tensionId === 'outer_inner_gap') {
    return {
      poleA: `Personality ${n.personality.value} (${digitWord(n.personality.value)})`,
      poleB: `Soul Urge ${n.soulUrge.value} (${digitWord(n.soulUrge.value)})`,
    };
  }
  if (tensionId === 'expression_path_gap') {
    return {
      poleA: `Expression (Destiny) ${n.expression.value} (${digitWord(n.expression.value)})`,
      poleB: `Life Path ${n.lifePath.value} (${digitWord(n.lifePath.value)})`,
    };
  }
  return { poleA: 'One part of the chart', poleB: 'Another part of the chart' };
}

function tensionIntegration(tensionId) {
  if (tensionId === 'desire_path_gap') {
    return 'Let the Life Path set the long tempo while Soul Urge gets weekly votes: small honest choices prevent long resentment.';
  }
  if (tensionId === 'outer_inner_gap') {
    return 'Treat Personality as a dial, not a mask — one or two true sentences early reduce the cost of performing okay.';
  }
  if (tensionId === 'expression_path_gap') {
    return 'Keep Expression as craft and Life Path as curriculum; they cooperate when you stop forcing them to match day to day.';
  }
  return 'Name both needs without making either one “wrong”; integration is usually alternating attention, not a perfect merge.';
}

export function buildTensionSection(bundle, theme) {
  const items = [];
  theme.tensions.forEach((t) => {
    const poles = tensionPoles(t.id, bundle);
    items.push({
      id: t.id,
      title: `${poles.poleA} ↔ ${poles.poleB}`,
      poleA: poles.poleA,
      poleB: poles.poleB,
      friction: t.summary,
      integration: tensionIntegration(t.id),
    });
  });

  const pyF = theme.personalYearFamily;
  const lpF = theme.lifePathFamily;
  if (
    items.length < 3 &&
    pyF &&
    lpF &&
    pyF !== lpF &&
    !items.some((x) => x.id === 'year_path_tone')
  ) {
    items.push({
      id: 'year_path_tone',
      title: `${familyPlainName(pyF, theme.familyLabels)} (this year) ↔ ${familyPlainName(lpF, theme.familyLabels)} (long arc)`,
      poleA: `Personal Year ${bundle.numbers.personalYear.value} — ${familyPlainName(pyF, theme.familyLabels)}`,
      poleB: `Life Path ${bundle.numbers.lifePath.value} — ${familyPlainName(lpF, theme.familyLabels)}`,
      friction:
        'The year’s climate and your long-arc story pull from different families. That can feel like the calendar asks for one thing while your deeper pattern asks for another.',
      integration:
        'Treat the year as weather: work with it for pace and projects without abandoning the long-arc skills you already paid for.',
    });
  }

  if (!items.length) {
    items.push({
      id: 'pace_sustainability',
      title: 'Momentum ↔ sustainability',
      poleA: 'What you want to move forward',
      poleB: 'What you can repeat without burning out',
      friction:
        'Your chart does not show a sharp split between the main core numbers — tension may show up less as “opposing digits” and more as calendar pressure versus honest capacity.',
      integration:
        'Let small, repeatable choices carry the week; save dramatic moves for when the year and your gut agree.',
    });
  }

  return items.slice(0, 4);
}

export function buildLifeAreas(bundle, theme) {
  const pref = String(bundle.meta.preferredLifeArea || '').trim();
  const prefLead =
    pref && pref !== 'timing'
      ? 'You asked for extra emphasis in one area — that block carries a slightly longer note.'
      : '';

  const n = bundle.numbers;
  const lp = n.lifePath.value;
  const su = n.soulUrge.value;
  const ex = n.expression.value;
  const pe = n.personality.value;
  const gap = theme.tensions.find((t) => t.id === 'outer_inner_gap');

  const areas = [
    {
      key: 'relationships',
      title: 'Relationships',
      theme: `Connection is filtered through ${digitWord(su)} wants and ${digitWord(pe)} first impressions.`,
      strength: gap
        ? `You can read a room quickly (Personality ${pe}), while your real needs run deeper (Soul Urge ${su}) — that is social intelligence when you name both.`
        : `Soul Urge ${su} and Life Path ${lp} shape what feels nourishing long-term, not only what feels exciting now.`,
      risk: gap
        ? 'Over-adapting to keep peace while quietly building resentment; performing compatibility instead of stating limits.'
        : 'Assuming others will infer your needs from how much you give — especially when Life Path leans responsible.',
      advice:
        'Lead with one concrete need per conversation; let repair be normal instead of proof the relationship failed.',
    },
    {
      key: 'work',
      title: 'Work and vocation',
      theme: `Contribution follows Expression ${ex} — how you execute — and Life Path ${lp} — what kinds of problems keep returning for you.`,
      strength: `You bring ${digitWord(ex)} into tasks: how you communicate, build, or hold a standard matters as much as the title.`,
      risk: 'Taking roles that look good on paper but starve Soul Urge motivation; confusing hustle with alignment.',
      advice: 'Design work so a weekly slice matches Expression strengths; let Life Path be the filter for which missions are worth your years.',
    },
    {
      key: 'money',
      title: 'Money and stability',
      theme: `Security questions hook into structure, care, and impact — digits ${n.birthday.value} (Birthday accent) and ${lp} (Life Path) often show up in how you tolerate risk.`,
      strength: `You can stabilize chaos when ${digitWord(lp)} is engaged — rhythm, responsibility, or discernment become practical tools.`,
      risk: 'Guilt about limits, rescuing others financially, or freezing when uncertainty hits — especially if care digits run high.',
      advice: 'Separate survival mechanics from identity; build one boring system (cash buffer, clear bills rhythm) so emotional labor is not paying for everything.',
    },
    {
      key: 'inner',
      title: 'Inner life',
      theme: `Private processing is shaped by Soul Urge ${su} and the long arc of Life Path ${lp}.`,
      strength: `You have a real inner compass around ${digitWord(su)} — it is worth protecting time to hear it.`,
      risk: 'Either spiraling alone too long or outsourcing inner clarity to noise (scroll, advice, urgency).',
      advice: 'Book short, repeatable solitude; come back with one sentence of truth, not a full verdict on your life.',
    },
    {
      key: 'expression',
      title: 'Communication and self-expression',
      theme: `Voice and visibility bridge Expression ${ex} and Personality ${pe}.`,
      strength: `When you speak from ${digitWord(ex)}, your work lands — people feel the coherence between idea and delivery.`,
      risk: 'Editing yourself into silence, or performing a version of you that earns approval but drains Soul Urge.',
      advice: 'Share earlier drafts of thoughts; let clarity be iterative so honesty does not require a perfect stage.',
    },
  ];

  return areas.map((a) => {
    if (pref && pref === a.key && prefLead) {
      return {
        ...a,
        theme: `${prefLead}\n\n${a.theme}`,
      };
    }
    return a;
  });
}

export function buildTimingSection(bundle, theme) {
  const n = bundle.numbers;
  const meta = bundle.meta;
  const py = n.personalYear.value;
  const pm = n.personalMonth.value;
  const p = packForDigit(py);
  const monthLabel = MONTH_NAMES[n.personalMonth.month] || String(n.personalMonth.month);

  let withChart = '';
  if (theme.timingResonance === 'aligned') {
    withChart = `This year’s tone and your Life Path family echo each other — the season may feel familiar, like practicing a skill you already own.`;
  } else if (theme.timingResonance === 'supportive_build') {
    withChart = `The year can still support your deeper motifs step by step — different costume, related lesson.`;
  } else {
    withChart = `Personal Year and Life Path do not match symbolically — use contrast as pacing information, not as proof you are off-track.`;
  }

  const pmPack = packForDigit(pm);
  return {
    headline: `Personal Year ${py} · ${meta.targetYear}`,
    yearEmphasis: joinSentences([
      `${p.word.charAt(0).toUpperCase() + p.word.slice(1)} shows up as the working question for ${meta.targetYear}: ${p.core}`,
    ]),
    goodFor: p.useWell.slice(0, 3),
    avoid: p.watchFor.slice(0, 3),
    withChart,
    monthLayer: {
      title: `Personal Month ${pm} · ${monthLabel}`,
      line: joinSentences([
        `A shorter wave inside the year: ${pmPack.word} flavor — ${pmPack.core}`,
        'Use it to pace projects and emotional bandwidth, not to override the Personal Year story.',
      ]),
    },
  };
}

export function buildFocusNow(bundle, theme) {
  const n = bundle.numbers;
  const bullets = [];
  const pLp = packForDigit(n.lifePath.value);
  const pSu = packForDigit(n.soulUrge.value);
  const pEx = packForDigit(n.expression.value);

  if (theme.tensions[0]?.id === 'outer_inner_gap') {
    bullets.push('Name one real limit early in social situations — before you perform okay.');
  }
  if (theme.tensions[0]?.id === 'desire_path_gap') {
    bullets.push('Give Soul Urge needs a weekly action, not only a long-term dream.');
  }
  if (theme.tensions[0]?.id === 'expression_path_gap') {
    bullets.push('Let Expression handle craft; let Life Path choose which problems are yours.');
  }

  if (theme.repeatedCoreDigits.length) {
    bullets.push(
      `Where ${theme.repeatedCoreDigits.join(', ')} repeats, choose one place to stop over-functioning — same gift, smaller radius.`,
    );
  }

  bullets.push(pLp.useWell[0]);
  bullets.push(pSu.useWell[1] || pSu.useWell[0]);
  bullets.push(pEx.useWell[2] || pEx.useWell[0]);

  if (theme.masterEcho) {
    bullets.push('Trade intensity for precision: smaller promises, cleaner follow-through.');
  }

  const uniq = [...new Set(bullets.filter(Boolean))];
  return uniq.slice(0, 5);
}

export function buildReflectionPrompts(bundle, theme) {
  const n = bundle.numbers;
  const prompts = [];
  const t0 = theme.tensions[0];
  if (t0?.id === 'desire_path_gap') {
    prompts.push('Where are you still acting as if the Life Path plan is noble, while your gut has already said no?');
  }
  if (t0?.id === 'outer_inner_gap') {
    prompts.push('What do you routinely hide behind “fine” that would cost less if it were spoken once plainly?');
  }
  if (t0?.id === 'expression_path_gap') {
    prompts.push('Which project are you forcing to prove your name-path, when the long arc actually wants a slower build?');
  }
  if (!prompts.length) {
    prompts.push(
      `Where does ${digitWord(n.lifePath.value)} show up as duty instead of choice — and what would change if it were chosen?`,
    );
  }
  prompts.push(
    `What would ${digitWord(n.soulUrge.value)} ask for this month if it did not have to sound reasonable?`,
  );
  prompts.push(
    theme.repeatedCoreDigits.length
      ? `When the “${theme.repeatedCoreDigits[0]} pattern” appears again, what is one boundary you will hold before you over-give?`
      : 'Which recurring frustration might be a boundary problem disguised as a personality problem?',
  );
  return prompts.slice(0, 3);
}

export function buildMethodology(bundle, theme) {
  const meta = bundle.meta;
  const n = bundle.numbers;
  const lp = n.lifePath.components;
  const lpPre = lp.month + lp.day + lp.year;

  const out = [];

  out.push(
    `Inputs used: birth date normalized to ${meta.birthDate}, full birth name as entered for letter-based numbers, timing year ${meta.targetYear} for Personal Year, and calendar month ${n.personalMonth.month} for Personal Month.`,
  );

  out.push(
    'Reduction rule: multi-digit totals are summed digit-by-digit until the value is 1–9, or a master 11, 22, or 33 is preserved as the final step. Karmic debt markers (13, 14, 16, 19) are recorded when they appear before the last reduction — naming lessons that may ask for patience, not punishment.',
  );

  out.push(
    `Life Path (${n.lifePath.value}): month → ${lp.month}, day → ${lp.day}, year ${meta.year} → ${lp.year}. Those reduced parts sum to ${lpPre}, then reduce to your Life Path.`,
  );

  out.push(
    `Expression (Destiny) (${n.expression.value}): Pythagorean letter sum of the full birth name totals ${n.expression.raw}, which reduces to this number.`,
  );

  out.push(
    `Soul Urge (${n.soulUrge.value}) and Personality (${n.personality.value}): vowels sum to ${n.soulUrge.raw} and consonants to ${n.personality.raw}, each reduced separately.`,
  );

  out.push(`Birthday (${n.birthday.value}): calendar day ${meta.day} is reduced to a gift-tone accent.`);

  out.push(
    `Maturity (${n.maturity.value}): Life Path ${n.lifePath.value} + Expression (Destiny) ${n.expression.value} = ${n.lifePath.value + n.expression.value}, then reduced — often read as how direction and capacity integrate over time.`,
  );

  const pyMonth = lp.month;
  const pyDay = lp.day;
  const pyYearPart = reducedYearComponent(meta.targetYear);
  const pySum = pyMonth + pyDay + pyYearPart;

  out.push(
    `Personal Year (${n.personalYear.value}): reduced birth month (${pyMonth}) + reduced birth day (${pyDay}) + reduced timing year ${meta.targetYear} (${pyYearPart}) → combined (${pySum}), then reduced.`,
  );

  out.push(
    `Personal Month (${n.personalMonth.value}): Personal Year ${n.personalYear.value} + calendar month ${n.personalMonth.month} → reduced — a shorter wave inside the year.`,
  );

  out.push(
    `How themes are ranked: each core number contributes to a weighted digit tally (Life Path and Expression (Destiny) carry the most weight; Soul Urge, Personality, Personal Year, Birthday, Maturity, and Personal Month follow). Repeats in the name grid add a small echo. Leading families — ${theme.topFamilies
      .slice(0, 3)
      .map((f) => theme.familyLabels[f])
      .join(', ')} — emerge from that combined score${
      theme.repeatedCoreDigits.length
        ? `, reinforced when ${theme.repeatedCoreDigits.join(', ')} repeats across core positions`
        : ''
    }.`,
  );

  if (meta.currentName) {
    out.push(
      'A current use-name was provided; where Expression (Destiny) differs from birth name, the report notes that as a lived negotiation rather than a contest over truth.',
    );
  }

  return out;
}

export function assembleReport(bundle, theme, _themeRecords) {
  const t = TIERS.FULL;
  const sections = [];

  if (tierAllows(t, SECTION_KEYS.reportSummary)) {
    sections.push({
      id: 'summary',
      title: 'Your chart at a glance',
      kind: 'summary',
      content: buildReportSummary(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.chartOneSentence)) {
    sections.push({
      id: 'sentence',
      title: 'Your chart in one sentence',
      kind: 'oneSentence',
      content: buildChartOneSentence(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.corePattern)) {
    sections.push({
      id: 'corepattern',
      title: 'Your core pattern',
      kind: 'corePattern',
      content: buildCorePattern(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.coreNumbers)) {
    sections.push({
      id: 'core',
      title: 'Core numbers',
      kind: 'coreInterpretation',
      content: buildCoreInterpretationBlocks(bundle),
    });
  }

  if (tierAllows(t, SECTION_KEYS.repeatingPatterns)) {
    sections.push({
      id: 'patterns',
      title: 'Your repeating patterns',
      kind: 'patterns',
      content: buildRepeatingPatterns(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.tension)) {
    sections.push({
      id: 'tension',
      title: 'Where tension shows up',
      kind: 'tension',
      content: buildTensionSection(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.lifeAreas)) {
    sections.push({
      id: 'life',
      title: 'Life areas',
      kind: 'life',
      content: buildLifeAreas(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.currentCycle)) {
    sections.push({
      id: 'cycle',
      title: 'Current cycle / timing',
      kind: 'timing',
      content: buildTimingSection(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.focusNow)) {
    sections.push({
      id: 'focus',
      title: 'What to focus on now',
      kind: 'focus',
      content: buildFocusNow(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.reflectionPrompts)) {
    sections.push({
      id: 'prompts',
      title: 'Reflection prompts',
      kind: 'list',
      content: buildReflectionPrompts(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.methodology)) {
    sections.push({
      id: 'method',
      title: 'How your chart was built',
      kind: 'methodology',
      content: buildMethodology(bundle, theme),
    });
  }

  if (tierAllows(t, SECTION_KEYS.advancedCourseParity)) {
    sections.push({
      id: 'advanced',
      title: 'Advanced chart topics',
      kind: 'advancedCourse',
      content: {
        intro:
          'This report focuses on the core numbers calculated above. The topics below are also part of a full chart. They are not computed by this generator — each card says that once and links to the matching course lesson where it exists.',
        items: buildAdvancedCourseParityTopics(),
      },
    });
  }

  return { tier: t, sections };
}
